import { QuoteStoreResponse, TravellerProductDetails } from '@trip-ux/redux-store';
import { get, gt, gte, isEmpty, last } from 'lodash';
import { ButtonVariant } from '../Button';
import { CardVariant, ProductCardAttributesValues, ProductCardGenericLabels } from './ProductCard';
import { ProductCardDropdown } from './ProductCardAttributes';
import { dayjs } from '@trip-ux/common';
import { DAYJS_DIFF } from '@trip-ux/constants';

export const defaultCardValidation = (
  deductibles: ProductCardDropdown[],
  tripDays: ProductCardDropdown[],
  sumInsuredAfterNeeded: boolean,
  values: ProductCardAttributesValues
): boolean => {
  const isValidDeductible = !isEmpty(deductibles) ? !isEmpty(values.deductible) : true;
  const isValidTripDays = !isEmpty(tripDays) ? !isEmpty(values.daysPerTrip) : true;
  const isValidSumInsuredAfter = sumInsuredAfterNeeded ? !isEmpty(values.sumInsuredAfter) : true;
  return isValidDeductible && isValidTripDays && isValidSumInsuredAfter;
};

export const coverMeCardValidation = (
  values: ProductCardAttributesValues,
  tripDays: ProductCardDropdown[],
  tripCost: boolean
): boolean => {
  if (!isEmpty(tripDays)) {
    return gt(values.daysPerTrip, 0) ? true : false;
  } else {
    return !tripCost || gte(values.tripCost, 0) ? true : false;
  }
};

export const validate = (
  values: ProductCardAttributesValues,
  deductibles: ProductCardDropdown[],
  tripDays: ProductCardDropdown[],
  sumInsuredAfterNeeded: boolean,
  tripCost: boolean,
  defaultType: boolean
): boolean => {
  return defaultType
    ? defaultCardValidation(deductibles, tripDays, sumInsuredAfterNeeded, values)
    : coverMeCardValidation(values, tripDays, tripCost);
};

export const buttonLabel = (isAddOn: boolean, isSelected: boolean, genericLabels: ProductCardGenericLabels): string => {
  if (isAddOn) {
    return isSelected ? genericLabels.addedOnPlanLabel : genericLabels.addonPlanLabel;
  }
  return isSelected ? genericLabels.selectedPlanLabel : genericLabels.selectPlanLabel;
};

export const mqTaken = (isMqRequired, isMqTaken) => !isMqRequired || isMqTaken;
export const isDefault = (variant: CardVariant) => isEmpty(variant) || variant === 'DEFAULT';

export const isCardValid = (
  values: ProductCardAttributesValues,
  deductibles: ProductCardDropdown[],
  tripDays: ProductCardDropdown[],
  sumInsuredAfterNeeded: boolean,
  tripCost: boolean,
  premium: number,
  cardVariant: CardVariant
): boolean => {
  const defaultType = isDefault(cardVariant);
  const isValidate = validate(values, deductibles, tripDays, sumInsuredAfterNeeded, tripCost, defaultType);
  return isValidate && premium > 0;
};

export const mqLabel = (isMqTaken: boolean, genericLabels: ProductCardGenericLabels): string =>
  isMqTaken ? genericLabels.mqEditLabel : genericLabels.mqLabel;

export type MuxButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'tertiary-back' | 'tertiary-noicon' | 'sign-in';

export const attributesNeeded = (
  deductibles?: ProductCardDropdown[],
  tripDays?: ProductCardDropdown[],
  sumInsuredAfterNeeded = false,
  tripCost = false,
  daysPerTrip: string = null
): boolean => !isEmpty(deductibles) || !isEmpty(tripDays) || sumInsuredAfterNeeded || tripCost || !isEmpty(daysPerTrip);

export const isPlanCardLoading = (
  travellersPlan: TravellerProductDetails,
  quote: QuoteStoreResponse,
  paxId: number,
  code: string
): boolean => {
  const planSelection = get(travellersPlan, 'tripParams');
  // checking last value to make sure spinner is enbled on clicked card
  const selectedPlan = planSelection && last(travellersPlan.tripParams.filter(param => param.paxId === paxId));
  return selectedPlan && selectedPlan.plan === code && get(quote, 'calculate', false);
};

export const invalidCalculatedTopUpEndDate = (
  tripStartDate: string,
  applicationDate: string,
  dateFormat: string,
  tripDaysMinTopUp: number,
  topUpPlanDays: number
): boolean => {
  const latestEndDate = applicationDate
    ? dayjs(applicationDate, dateFormat).startOf(DAYJS_DIFF.DAYS)
    : dayjs().startOf(DAYJS_DIFF.DAYS);
  const calculatedEndDate = dayjs(tripStartDate, dateFormat)
    .startOf(DAYJS_DIFF.DAYS)
    .add(tripDaysMinTopUp ?? 0, DAYJS_DIFF.DAYS)
    .add(topUpPlanDays, DAYJS_DIFF.DAYS)
    .subtract(1, DAYJS_DIFF.DAYS);

  return calculatedEndDate.isBefore(latestEndDate);
};
